import {
    ModalBody,
    ModalCloseButton,
    ModalFooter,
    ModalHeader,
    ModalParagraph,
    ModalTitle
} from '@components/ui/modal-atoms'
import {Button} from '@components/ui/button'
import {useTranslation, TKey} from '@/translations/i18n.tsx'
import {cn} from '@utilities/helpers.ts'
import {Dispatch, HTMLAttributes, SetStateAction, useState} from 'react'
import {Spinner} from '@components/ui/spinner'
import {ModalName} from '@features/assignment/components/schedule-buttons'
import {useAssignmentCoordination} from '@features/assignment/queries/useAssignmentCoordination'
import {ServiceType} from '@features/assignment/types.ts'
import {Dialog, DialogClose, DialogContent} from '@components/ui/dialog'

const Option = ({isActive, className, ...props}: HTMLAttributes<HTMLDivElement> & {isActive: boolean}) => (
    <div
        className={cn(
            'flex w-full cursor-pointer flex-col gap-1 rounded-xl border px-4 py-3 text-sm [&_h4]:font-medium [&_p]:text-neutral-700',
            isActive ? 'border-primary-600 bg-primary-50' : 'border-neutral-300 bg-white',
            className
        )}
        {...props}
    />
)

export const CannotScheduleModal = ({
    onClose,
    assignmentId,
    taskId,
    setOpenModal,
    serviceType
}: {
    onClose: () => void
    assignmentId: number
    taskId: number
    setOpenModal: Dispatch<SetStateAction<ModalName | null>>
    serviceType: ServiceType
}) => {
    const {t} = useTranslation()
    const [selectedOption, setSelectedOption] = useState<
        null | 'unresponsive_host' | 'host_not_ready' | 'not_available' | 'dates_dont_match'
    >(null)
    const dontMatchDatesMutation = useAssignmentCoordination({assignmentId, taskId})

    const onSubmit = () => {
        if (selectedOption == 'dates_dont_match') {
            dontMatchDatesMutation.mutate({status: 'dates_dont_match'})
        }
        if (selectedOption == 'host_not_ready') {
            setOpenModal('host_not_ready')
        }
        if (selectedOption == 'not_available') {
            setOpenModal('not_available')
        }
        if (selectedOption == 'unresponsive_host') {
            setOpenModal('unresponsive_host')
        }
    }

    return (
        <Dialog open={true} onOpenChange={onClose}>
            <DialogContent className="w-120" showCloseButton={false}>
                <ModalHeader>
                    <DialogClose data-slot="dialog-close" render={<ModalCloseButton />} />
                </ModalHeader>
                <ModalBody>
                    <div className="flex flex-col gap-2">
                        <ModalTitle>{t('assignment:descriptions:cannot_schedule_modal:title')}</ModalTitle>
                        <ModalParagraph>{t('assignment:descriptions:cannot_schedule_modal:subtitle')}</ModalParagraph>
                    </div>
                    <div className="flex flex-col gap-4">
                        <Option
                            isActive={selectedOption == 'unresponsive_host'}
                            onClick={() => setSelectedOption('unresponsive_host')}
                        >
                            <h4>{t('assignment:descriptions:cannot_schedule_modal:option1_title')}</h4>
                            <p>{t('assignment:descriptions:cannot_schedule_modal:option1_description')}</p>
                        </Option>
                        <Option
                            isActive={selectedOption == 'host_not_ready'}
                            onClick={() => setSelectedOption('host_not_ready')}
                        >
                            <h4>{t('assignment:descriptions:cannot_schedule_modal:option2_title')}</h4>
                            <p>
                                {t(
                                    `assignment:descriptions:cannot_schedule_modal:option2_description:${serviceType}` as TKey
                                )}
                            </p>
                        </Option>
                        <Option
                            isActive={selectedOption == 'not_available'}
                            onClick={() => setSelectedOption('not_available')}
                        >
                            <h4>{t(`assignment:descriptions:cannot_schedule_modal:option3_title`)}</h4>
                            <p>{t('assignment:descriptions:cannot_schedule_modal:option3_description')}</p>
                        </Option>
                        <Option
                            isActive={selectedOption == 'dates_dont_match'}
                            onClick={() => setSelectedOption('dates_dont_match')}
                        >
                            <h4>{t('assignment:descriptions:cannot_schedule_modal:option4_title')}</h4>
                            <p>
                                {t(
                                    `assignment:descriptions:cannot_schedule_modal:option4_description:${serviceType}` as TKey
                                )}
                            </p>
                        </Option>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button variant="tertiary" onClick={onClose}>
                        {t('commons:cancel')}
                    </Button>
                    <Button disabled={!selectedOption || dontMatchDatesMutation.isPending} onClick={onSubmit}>
                        {selectedOption == 'dates_dont_match'
                            ? t('commons:confirm')
                            : t('assignment:descriptions:cannot_schedule_modal:cta')}
                        {dontMatchDatesMutation.isPending && <Spinner size={20} />}
                    </Button>
                </ModalFooter>
            </DialogContent>
        </Dialog>
    )
}
